import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from "react";
import { useAuth } from "./AuthContext";
import { envsApi } from "../services/api";

const EnvironmentContext = createContext(null);

const ALL_ENV_OPTIONS = [
  { value: "dev", label: "Development", color: "#52c41a" },
  { value: "stage", label: "Staging", color: "#fa8c16" },
  { value: "prod", label: "Production", color: "#f5222d" },
];

export function EnvironmentProvider({ children }) {
  const { isAuthenticated } = useAuth();
  const [selectedEnv, setSelectedEnv] = useState("dev");
  const [accessibleEnvs, setAccessibleEnvs] = useState([]);
  const [loaded, setLoaded] = useState(false);

  // Fetch accessible envs from server on auth change
  useEffect(() => {
    if (!isAuthenticated) {
      setAccessibleEnvs([]);
      setLoaded(false);
      return;
    }

    let cancelled = false;
    envsApi.getAvailable()
      .then((res) => {
        if (!cancelled) {
          setAccessibleEnvs(res.data.envs || []);
          setLoaded(true);
        }
      })
      .catch(() => {
        if (!cancelled) {
          setAccessibleEnvs([]);
          setLoaded(true);
        }
      });

    return () => { cancelled = true; };
  }, [isAuthenticated]);

  const allowedEnvs = useMemo(() => {
    if (!loaded) return ALL_ENV_OPTIONS; // Show all while loading to avoid flash
    if (accessibleEnvs.length === 0) return []; // User has no env access
    return ALL_ENV_OPTIONS.filter((opt) => accessibleEnvs.includes(opt.value));
  }, [accessibleEnvs, loaded]);

  // If selected env is not in allowed list, fall back to first allowed
  const effectiveEnv = useMemo(() => {
    const env = allowedEnvs.some((e) => e.value === selectedEnv)
      ? selectedEnv
      : (allowedEnvs[0]?.value || "dev");
    localStorage.setItem("selected_env", env);
    return env;
  }, [selectedEnv, allowedEnvs]);

  const changeEnv = useCallback((env) => {
    setSelectedEnv(env);
    // Keep localStorage in sync so axios interceptor can read the current env for x-environment header
    localStorage.setItem("selected_env", env);
  }, []);

  // Allow re-fetching envs (called after access changes)
  const refreshEnvs = useCallback(() => {
    if (!isAuthenticated) return;
    envsApi.getAvailable()
      .then((res) => setAccessibleEnvs(res.data.envs || []))
      .catch(() => {});
  }, [isAuthenticated]);

  return (
    <EnvironmentContext.Provider value={{ selectedEnv: effectiveEnv, changeEnv, envOptions: allowedEnvs, refreshEnvs }}>
      {children}
    </EnvironmentContext.Provider>
  );
}

export function useEnvironment() {
  const ctx = useContext(EnvironmentContext);
  if (!ctx) throw new Error("useEnvironment must be used within EnvironmentProvider");
  return ctx;
}
